/**
 * Main App component with routing and authentication
 * v2.0.0 - Mobile-first dashboard with stats pages
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { Layout } from './components/Layout';
import { SilviaLoader } from './components/SilviaLoader';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Orders } from './pages/Orders';
import { OrderDetail } from './pages/OrderDetail';
import { Payments } from './pages/Payments';
import { History } from './pages/History';
import { Stats } from './pages/Stats';
import { Finance } from './pages/Finance';
import { Performance } from './pages/Performance';
import { OrderAnalytics } from './pages/OrderAnalytics';
import { WcOrders } from './pages/WcOrders';
import { WcOrderDetail as WcOrderDetailPage } from './pages/WcOrderDetail';
import { Complaints } from './pages/Complaints';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <SilviaLoader variant="screen" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return <SilviaLoader variant="screen" />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to="/stats" replace /> : <Login />}
        />

        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Stats/Analytics pages (mobile-first) */}
          <Route path="/stats" element={<Stats />} />
          <Route path="/finance" element={<Finance />} />
          <Route path="/performance" element={<Performance />} />
          <Route path="/order-analytics" element={<OrderAnalytics />} />
          {/* Complaints */}
          <Route path="/complaints" element={<Complaints />} />
          {/* WC Order Management (CRUD) */}
          <Route path="/wc-orders" element={<WcOrders />} />
          <Route path="/wc-orders/:orderId" element={<WcOrderDetailPage />} />
          {/* Bot management pages */}
          <Route path="/bot" element={<Dashboard />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:orderId" element={<OrderDetail />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/history" element={<History />} />
          {/* Default: redirect to dashboard */}
          <Route path="/" element={<Navigate to="/stats" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/stats" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
